import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import useDocumentMeta from "../hooks/useDocumentMeta";
import { Cpu, Lock, FileLock2, Receipt, Activity, Eye, EyeOff, ShieldCheck, ArrowUpRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Personal Advancement and Career Training — public product showcase.
 *
 * The "wireframe panel" now reads live aggregate stats from
 * /api/v1/public/community-impact (ucms_wireframe block). No PII —
 * the encrypted resident column stays visually locked (that's the
 * whole point of this section).
 */
export default function UCMS() {
  useDocumentMeta({
    title:       "Personal Advancement and Career Training (PACT) · Encrypted Care Coordination | The Exchange Alliance",
    description: "PACT is a secure bridge connecting independent clinics, shelters, and food assistance programs: consent-based care coordination, a secure document vault, a weekly spending ledger, and multi-agency expense allocation.",
    keywords:    "Personal Advancement and Career Training, PACT, HIPAA case management software, encrypted resident records, transitional workforce housing coordination, consent-based care coordination",
    canonical:   "https://theexchangealliance.com/personal-advancement-career-training",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [snap, setSnap] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/site/v1/public/community-impact?window=all`, { cache: "no-store" });
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled) setSnap(j);
      } catch { /* offline — fall back to zeroed defaults */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const ucms = snap?.ucms_wireframe || null;
  const stats = buildStats(ucms);
  const bars  = buildBars(ucms);
  const rowCount = Math.max(1, Math.min(8, ucms?.active_residents ?? 8));

  return (
    <main data-testid="ucms-root" className="bg-[#FAFAF7] text-[#0A0612] font-sans">
      <Header />

      <section className="pt-32 pb-16 sm:pt-40 sm:pb-24 border-b border-[#E8E5DA]">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#C9A227] mb-3 flex items-center gap-2">
            <Cpu className="h-3 w-3"/> Personal Advancement and Career Training · PACT
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A0612] leading-[1.05] max-w-4xl">
            A secure bridge.<br/>
            <span className="text-[#6B6F76]">Three phases.</span><br/>
            Encrypted end-to-end.
          </h1>
          <p className="max-w-2xl text-base sm:text-lg text-[#6B6F76] mt-6 leading-relaxed">
            PACT is a secure switchboard between independent community partners — a coordination
            utility that saves case managers redundant phone calls and spares people from retelling
            their story at every stop. Residents may choose who can see their records. Built for
            transparency with auditors — built for the privacy of the Last, the Least and the Lost we serve.
          </p>
        </div>
      </section>

      {/* Data integrity protocols */}
      <section className="py-20 sm:py-28 border-b border-[#E8E5DA] bg-white">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#C9A227] mb-3">Data Integrity Protocols</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#0A0612] mb-12 max-w-3xl">Every record traceable. Every signature timestamped. Every dollar reconciled.</h2>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {PROTOCOLS.map((p) => (
              <article key={p.title} data-testid={`ucms-protocol-${p.title.toLowerCase().replace(/\s+/g,'-')}`} className="bg-[#FAFAF7] border border-[#E8E5DA] p-7 lg:p-9 group hover:bg-white transition-colors" style={{ borderTop: `3px solid ${p.accent}` }}>
                <p.icon className="h-6 w-6 mb-5" style={{ color: p.accent }} strokeWidth={1.5}/>
                <h3 className="font-display font-bold text-xl tracking-tight text-[#0A0612] mb-2">{p.title}</h3>
                <p className="text-sm text-[#6B6F76] leading-relaxed">{p.body}</p>
                <div className="mt-5 pt-4 border-t border-[#E8E5DA] font-mono text-[9px] tracking-[0.22em] uppercase text-[#6B6F76]">
                  {p.detail}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Live audit lens — transparency vs encryption */}
      <section className="py-20 sm:py-28 border-b border-[#E8E5DA]">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#C9A227] mb-3">How Auditors See It</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#0A0612] mb-3 max-w-3xl">Transparent aggregates. Encrypted identities.</h2>
          <p className="text-[#6B6F76] text-base max-w-3xl mb-12">
            Auditors and alliance stakeholders see real-time aggregate data — bed occupancy, cost
            allocations, phase velocity. The underlying resident records stay encrypted at the field
            level. No one — not auditors, not the SA, not the database operator — sees PII without
            a role-credentialed unlock that is itself audited.
          </p>

          <div className="bg-white border border-[#E8E5DA] shadow-sm" data-testid="ucms-wireframe">
            {/* Mock chrome */}
            <div className="border-b border-[#E8E5DA] px-5 py-3 flex flex-wrap items-center gap-2 bg-[#FAFAF7]">
              <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]"/>
              <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]"/>
              <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]"/>
              <span className="ml-3 min-w-0 break-all font-mono text-[10px] tracking-[0.22em] uppercase text-[#6B6F76]">theexchangealliance.com/admin/audit-mode</span>
              <span className="ml-auto inline-flex items-center gap-1.5 font-mono text-[9px] tracking-[0.22em] uppercase text-[#15803D]">
                <ShieldCheck className="h-3 w-3"/> {snap ? "Live · Read-only audit lens" : "Loading…"}
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-[#E8E5DA]">
              {/* Stats column — visible to auditors */}
              <div className="lg:col-span-7 min-w-0 bg-white p-6 sm:p-8">
                <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#15803D] mb-4 inline-flex items-center gap-1.5"><Eye className="h-3 w-3"/> Visible · Aggregate · Real-time</div>
                <div className="grid grid-cols-2 gap-3">
                  {stats.map((s) => (
                    <div key={s.testid} data-testid={s.testid} className="bg-[#FAFAF7] border border-[#E8E5DA] p-4">
                      <div className="font-display font-bold text-2xl text-[#0A0612]">{s.v}</div>
                      <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#6B6F76] mt-1">{s.l}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-6">
                  <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#6B6F76] mb-2">
                    Phase Distribution · Active pipeline
                  </div>
                  <div className="space-y-2">
                    {bars.map((b) => (
                      <div key={b.label} data-testid={`ucms-bar-${b.label.toLowerCase().replace(/\s+/g,'-')}`} className="flex items-center gap-3">
                        <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#0A0612] w-24 shrink-0">{b.label}</span>
                        <div className="flex-1 h-2.5 bg-[#FAFAF7] border border-[#E8E5DA] overflow-hidden">
                          <div className="h-full transition-all duration-500" style={{ width: `${b.pct}%`, background: b.color }}/>
                        </div>
                        <span className="font-mono text-[10px] text-[#6B6F76] w-12 text-right shrink-0">{b.pct}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Resident grid — encrypted column (visual only, no PII) */}
              <div className="lg:col-span-5 min-w-0 bg-[#0A0612] text-white p-6 sm:p-8">
                <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#DC2626] mb-4 inline-flex items-center gap-1.5"><EyeOff className="h-3 w-3"/> Encrypted · PII · Locked</div>
                <div className="space-y-2.5" data-testid="ucms-encrypted-rows">
                  {Array.from({ length: rowCount }).map((_, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 px-3 py-2.5 bg-white/[0.05] border border-white/10">
                      <div className="flex items-center gap-3 min-w-0">
                        <Lock className="h-3.5 w-3.5 text-[#D4AF37] shrink-0"/>
                        <span className="font-mono text-[10px] tracking-[0.15em] text-white/40 truncate">
                          {"█".repeat(8 + (i % 5))} · {"●".repeat(6)}-{"●".repeat(4)}
                        </span>
                      </div>
                      <span className="font-mono text-[9px] text-[#D4AF37]/70 shrink-0">PH {(i % 3) + 1}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-5 pt-4 border-t border-white/10 text-[11px] leading-relaxed text-white/55">
                  Name, SSN-4, DOB, and clinical fields are AES-encrypted at the application layer with
                  a key never exposed to the database operator. Even Super Admin sees a structured cipher
                  block in this view unless they explicitly unlock a participant — and that unlock is
                  itself written to the immutable audit log.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-28 bg-[#0A0612] text-white">
        <div className="mx-auto max-w-3xl px-6 lg:px-8 text-center">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] mb-3">For Alliance-Curious Agencies</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mb-5">See PACT running on your caseload.</h2>
          <p className="text-white/65 text-base max-w-xl mx-auto leading-relaxed">
            Apply for Alliance Partnership and the CAO will schedule a live walkthrough on your own
            (de-identified) data set. Within 30 days your agency can be linked in and reading from
            the same case-management spine our three founding nonprofits already use.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/alliances#apply" data-testid="ucms-cta-apply" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#D4AF37] hover:bg-[#F4D88A] text-[#0A0612] font-mono text-[11px] tracking-[0.22em] uppercase">
              Apply for Alliance Partnership <ArrowUpRight className="h-4 w-4"/>
            </Link>
            <a href="/login" data-testid="ucms-cta-portal" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-transparent border border-white/30 hover:border-white text-white font-mono text-[11px] tracking-[0.22em] uppercase">
              <Lock className="h-3.5 w-3.5"/> Existing Staff · Portal Login
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}

function fmt(n) {
  return typeof n === "number" ? n.toLocaleString("en-US") : "—";
}
function fmtUSD(n) {
  if (typeof n !== "number") return "—";
  return "$" + n.toLocaleString("en-US");
}

function buildStats(ucms) {
  if (!ucms) {
    return [
      { testid: "ucms-stat-active",     v: "—", l: "Active Residents" },
      { testid: "ucms-stat-occupancy",  v: "—", l: "Bed Occupancy" },
      { testid: "ucms-stat-cm-pool",    v: "—", l: "CM Pool · Net" },
      { testid: "ucms-stat-agencies",   v: "—", l: "Member Agencies" },
    ];
  }
  return [
    { testid: "ucms-stat-active",    v: fmt(ucms.active_residents),           l: "Active Residents" },
    { testid: "ucms-stat-occupancy", v: `${ucms.occupancy_pct ?? 0}%`,        l: "Bed Occupancy" },
    { testid: "ucms-stat-cm-pool",   v: fmtUSD(ucms.q_cm_pool_net),           l: `${ucms.q_label || "Q"} CM Pool · Net` },
    { testid: "ucms-stat-agencies",  v: fmt(ucms.member_agencies),            l: "Member Agencies" },
  ];
}

function buildBars(ucms) {
  const pb = ucms?.phase_breakdown || {};
  return [
    { label: "Phase I",   pct: pb.phase_1_pct ?? 0, color: "#7C3AED" },
    { label: "Phase II",  pct: pb.phase_2_pct ?? 0, color: "#C9A227" },
    { label: "Phase III", pct: pb.phase_3_pct ?? 0, color: "#1E40AF" },
  ];
}

const PROTOCOLS = [
  { icon: Activity, accent: "#7C3AED", title: "Training & Workforce Readiness Milestones",
    body: "Every resident moves through I → V across stabilization, training, and Launch Verification. Phase transitions are timestamped and audited, and shared only with the member agencies the resident has chosen to collaborate with — without anyone re-keying data.",
    detail: "AES field-level encryption · Audit trail · Participant-permissioned sharing" },
  { icon: FileLock2, accent: "#C9A227", title: "Secure Document Vault",
    body: "Lease addenda, signed consents, IDs, and clinical paperwork sit in a per-resident vault with role-based unlock + tamper-evident hashes. Documents stay encrypted at rest with a key the database operator never sees.",
    detail: "SHA-256 integrity hash · Per-document RBAC unlock" },
  { icon: Receipt, accent: "#1E40AF", title: "Live Weekly Spending Ledger",
    body: "Resident paychecks, contributions to case management, and individual disposition spend reconcile on the same cadence as our pay cycle. Auditors get a Friday afternoon snapshot before the bank does.",
    detail: "Real-time clearinghouse · Friday snapshot · Audited ledger" },
  { icon: ShieldCheck, accent: "#15803D", title: "Staff Expense Allocation",
    body: "Field staff snap a receipt on mobile, Gemini OCR extracts merchant + total, and SA/CAO route the cost to the right Life Foundation spending envelope in one pick. Reimbursable payables post instantly.",
    detail: "Mobile OCR · One-pick envelope routing · Audited reconcile" },
];
